// Stack of cubes (Year 7, 1 Nov 2026). A `cube_stack` is given by how tall
// each column of cubes is: `heights` lists the rows from the front to the
// back, each row from left to right. With no `views` it is drawn in 3D on a
// isometric, seen from the front, the right and above, sitting on a
// flat grid, with an arrow marked "front". With `views` it is drawn only as
// those flat views, each worked out from the heights, so a view always
// matches its stack. The side view is always the view from the right.
//
// What a screen reader says never counts the cubes (wording approved by
// the owner, 1 Nov 2026).
//
// Pure parsing and geometry only, no components (see
// practice-diagram-data.ts).

export type CubeView = 'front' | 'side' | 'top';

export type CubeStackSpec = {
  type: 'cube_stack';
  // Rows from front to back; columns from left to right.
  heights: number[][];
  // None: the stack in 3D.
  views: CubeView[];
  // A caption under each view.
  captions: boolean;
};

const MAX = { rows: 4, columns: 5, height: 5 };
const VIEWS: CubeView[] = ['front', 'side', 'top'];

export function readCubeStack(
  record: Record<string, unknown>,
): CubeStackSpec | null {
  const { heights, views, captions } = record;
  if (!Array.isArray(heights) || heights.length < 1) return null;
  if (heights.length > MAX.rows) return null;
  const rows: number[][] = [];
  let width: number | null = null;
  let total = 0;
  for (const row of heights as unknown[]) {
    if (!Array.isArray(row) || row.length < 1 || row.length > MAX.columns)
      return null;
    if (width !== null && row.length !== width) return null;
    width = row.length;
    const columns: number[] = [];
    for (const height of row as unknown[]) {
      if (
        typeof height !== 'number' ||
        !Number.isInteger(height) ||
        height < 0 ||
        height > MAX.height
      )
        return null;
      columns.push(height);
      total += height;
    }
    rows.push(columns);
  }
  if (total === 0) return null;
  let shown: CubeView[] = [];
  if (views !== undefined) {
    if (!Array.isArray(views) || views.length < 1) return null;
    for (const view of views as unknown[]) {
      const known = VIEWS.find((name) => name === view);
      if (!known || shown.includes(known)) return null;
      shown.push(known);
    }
    // Always drawn front, side, top, whatever order they were given in.
    shown = VIEWS.filter((name) => shown.includes(name));
  }
  if (captions !== undefined && typeof captions !== 'boolean') return null;
  return {
    type: 'cube_stack',
    heights: rows,
    views: shown,
    captions: captions ?? shown.length > 1,
  };
}

function spokenViews(views: CubeView[]): string {
  const named = views.map((view) => `the ${view} view`);
  if (named.length < 2) return named.join('');
  return `${named.slice(0, -1).join(', ')} and ${named[named.length - 1] ?? ''}`;
}

export function describeCubeStack(stack: CubeStackSpec): string {
  if (stack.views.length === 0)
    return 'A drawing of cubes stacked on a flat surface, seen from the front and slightly above. An arrow shows the front.';
  const views = spokenViews(stack.views);
  return `${views.charAt(0).toUpperCase()}${views.slice(1)} of a stack of cubes, drawn as squares.`;
}

// ---------------------------------------------------------------------------
// Where everything is drawn, in units close to one pixel each at the
// picture's natural width.

export type CubePoint = { x: number; y: number };
export type CubeFace = { points: CubePoint[]; side: 'front' | 'top' | 'right' };

export type CubeStackPicture = {
  // The flat grid the stack stands on (3D only).
  floor: CubePoint[][];
  // Painted in order, back to front, so nearer cubes cover further ones.
  faces: CubeFace[];
  // The arrow that shows the front, and its word.
  arrow: { from: CubePoint; to: CubePoint; word: CubePoint } | null;
  captions: (CubePoint & { text: string })[];
  box: { x: number; y: number; width: number; height: number };
};

// The 3D stack is drawn in true isometric projection: all three edges of a
// cube are the same length, and the left-to-right and front-to-back edges
// both slope at 30 degrees to the horizontal (columns run down to the
// right, rows run back up to the right, heights go straight up).
const CUBE = 26;
const ACROSS = CUBE * Math.cos(Math.PI / 6);
const SLOPE = CUBE * Math.sin(Math.PI / 6);
// Which way "back" points on the page (a unit vector), for the arrow.
const BACKWARD = { x: Math.cos(Math.PI / 6), y: -Math.sin(Math.PI / 6) };
const SQUARE = 24;
const VIEW_GAP = 30;
const CAPTION_GAP = 18;
const ARROW = { gap: 12, length: 26, word: 14 };
export const CUBE_CAPTION_SIZE = 13;
const PAD = 8;

const CAPTION: Record<CubeView, string> = {
  front: 'Front view',
  side: 'Side view',
  top: 'Top view',
};

function square(x: number, y: number, size: number): CubePoint[] {
  return [
    { x, y },
    { x: x + size, y },
    { x: x + size, y: y + size },
    { x, y: y + size },
  ];
}

// The arrow points at the front edge along `toward` (a unit vector), from a
// little way off it, with its word beyond its tail.
function frontArrow(
  edge: CubePoint,
  toward: CubePoint = { x: 0, y: -1 },
): CubeStackPicture['arrow'] {
  const along = (distance: number) => ({
    x: edge.x - toward.x * distance,
    y: edge.y - toward.y * distance,
  });
  return {
    from: along(ARROW.gap + ARROW.length),
    to: along(ARROW.gap),
    word: along(ARROW.gap + ARROW.length + ARROW.word),
  };
}

function stackPicture(heights: number[][]): Omit<CubeStackPicture, 'box'> {
  const rows = heights.length;
  const columns = heights[0]?.length ?? 0;
  const corner = (column: number, row: number, level: number) => ({
    x: (column + row) * ACROSS,
    y: (column - row) * SLOPE - level * CUBE,
  });
  const has = (column: number, row: number, level: number) =>
    level < (heights[row]?.[column] ?? 0);
  const floor: CubePoint[][] = [];
  for (let row = 0; row < rows; row += 1)
    for (let column = 0; column < columns; column += 1)
      floor.push([
        corner(column, row, 0),
        corner(column + 1, row, 0),
        corner(column + 1, row + 1, 0),
        corner(column, row + 1, 0),
      ]);
  // Seen from the front, the right and above, a cube is nearer the further
  // right, the further forward and the higher it is. Cubes the same distance
  // away never overlap in isometric, so painting in that order hides exactly
  // what a real drawing hides. A face pressed against another cube is left
  // out altogether.
  const cubes: { column: number; row: number; level: number }[] = [];
  for (let row = 0; row < rows; row += 1)
    for (let column = 0; column < columns; column += 1)
      for (let level = 0; has(column, row, level); level += 1)
        cubes.push({ column, row, level });
  const near = (cube: (typeof cubes)[number]) =>
    cube.column - cube.row + cube.level;
  cubes.sort((one, two) => near(one) - near(two));
  const faces: CubeFace[] = [];
  for (const { column, row, level } of cubes) {
    const at = (c: number, r: number, l: number) =>
      corner(column + c, row + r, level + l);
    if (!has(column, row - 1, level))
      faces.push({
        side: 'front',
        points: [at(0, 0, 0), at(1, 0, 0), at(1, 0, 1), at(0, 0, 1)],
      });
    if (!has(column, row, level + 1))
      faces.push({
        side: 'top',
        points: [at(0, 0, 1), at(1, 0, 1), at(1, 1, 1), at(0, 1, 1)],
      });
    if (!has(column + 1, row, level))
      faces.push({
        side: 'right',
        points: [at(1, 0, 0), at(1, 1, 0), at(1, 1, 1), at(1, 0, 1)],
      });
  }
  return {
    floor,
    faces,
    // Along the back-to-front direction, at the middle of the front edge.
    arrow: frontArrow(corner(columns / 2, 0, 0), BACKWARD),
    captions: [],
  };
}

function viewSquares(
  heights: number[][],
  view: CubeView,
): { squares: CubePoint[][]; width: number; height: number } {
  const rows = heights.length;
  const columns = heights[0]?.length ?? 0;
  const tallest = (values: number[]) => Math.max(0, ...values);
  const squares: CubePoint[][] = [];
  if (view === 'top') {
    // The front row is at the bottom.
    for (let row = 0; row < rows; row += 1)
      for (let column = 0; column < columns; column += 1)
        if ((heights[row]?.[column] ?? 0) > 0)
          squares.push(
            square(column * SQUARE, (rows - 1 - row) * SQUARE, SQUARE),
          );
    return { squares, width: columns * SQUARE, height: rows * SQUARE };
  }
  // Seen from the right, the back is on the left and the front on the right.
  const stacks =
    view === 'front'
      ? Array.from({ length: columns }, (_, column) =>
          tallest(heights.map((row) => row[column] ?? 0)),
        )
      : Array.from({ length: rows }, (_, place) =>
          tallest(heights[rows - 1 - place] ?? []),
        );
  const high = tallest(stacks);
  for (const [place, count] of stacks.entries())
    for (let level = 0; level < count; level += 1)
      squares.push(square(place * SQUARE, (high - 1 - level) * SQUARE, SQUARE));
  return { squares, width: stacks.length * SQUARE, height: high * SQUARE };
}

function viewsPicture(stack: CubeStackSpec): Omit<CubeStackPicture, 'box'> {
  const drawn = stack.views.map((view) => ({
    view,
    ...viewSquares(stack.heights, view),
  }));
  // Every view stands on the same bottom line.
  const bottom = Math.max(...drawn.map((view) => view.height));
  const faces: CubeFace[] = [];
  const captions: CubeStackPicture['captions'] = [];
  let arrow: CubeStackPicture['arrow'] = null;
  let left = 0;
  for (const view of drawn) {
    const drop = bottom - view.height;
    for (const points of view.squares)
      faces.push({
        side: view.view === 'top' ? 'top' : 'front',
        points: points.map((point) => ({
          x: point.x + left,
          y: point.y + drop,
        })),
      });
    const middle = left + view.width / 2;
    // A top view shows which way is the front.
    if (view.view === 'top') arrow = frontArrow({ x: middle, y: bottom });
    if (stack.captions)
      captions.push({
        x: middle,
        y:
          bottom +
          (view.view === 'top'
            ? ARROW.gap + ARROW.length + ARROW.word + CAPTION_GAP
            : CAPTION_GAP),
        text: CAPTION[view.view],
      });
    left += view.width + VIEW_GAP;
  }
  // Captions line up under the lowest thing drawn.
  const line = Math.max(0, ...captions.map((caption) => caption.y));
  return {
    floor: [],
    faces,
    arrow,
    captions: captions.map((caption) => ({ ...caption, y: line })),
  };
}

export function cubeStackPicture(stack: CubeStackSpec): CubeStackPicture {
  const picture =
    stack.views.length === 0
      ? stackPicture(stack.heights)
      : viewsPicture(stack);
  const points = [
    ...picture.floor.flat(),
    ...picture.faces.flatMap((face) => face.points),
  ];
  if (picture.arrow) {
    const { word } = picture.arrow;
    points.push(
      { x: word.x - 22, y: word.y - 8 },
      { x: word.x + 22, y: word.y + 8 },
    );
  }
  for (const caption of picture.captions)
    points.push(
      { x: caption.x - caption.text.length * 4, y: caption.y - 8 },
      { x: caption.x + caption.text.length * 4, y: caption.y + 8 },
    );
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const x = Math.min(...xs) - PAD;
  const y = Math.min(...ys) - PAD;
  return {
    ...picture,
    box: {
      x,
      y,
      width: Math.max(...xs) + PAD - x,
      height: Math.max(...ys) + PAD - y,
    },
  };
}
